import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Skeleton from '../../atoms/Skeleton';
import './brandAbout.css';

/* ABOUT THE BRAND — the React port of `brandAboutSections()` (SP-main
   `js/shopdot.js` l.1441).

   One source for both surfaces, which is why it is a component: the reference
   lifted this content out of the two places that drew it because they had
   already drifted ("written out by hand on the brand page and missing entirely
   from the product page"). The brand page is its only consumer here; the product
   page's "About the brand" takes this component, not a copy.

   Section order and headings are the reference's: Overview, Brand values,
   Shipping, Return & refund policy in the main panel, Connect beside them —
   "Brand values" and "Return & refund policy", not Title Case.

   A section with nothing behind it is not drawn: every block is guarded on its
   own field, because a real partner may have filled in three of six. */

/* The three fact glyphs, inline for the same reason the reference inlines them:
   they are part of the content, not an icon set this page imports. */
const CalendarIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" />
  </svg>
);
const MailIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M4 7l8 6 8-6" />
  </svg>
);
const PlayIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
);

/* The poster is a <button>, not a div with a click handler: the reference needs
   `role="button" tabindex="0"` plus a delegated keydown listener because it
   works from a string of HTML. In React the real element brings focus, Enter,
   Space and the announcement with it. */
function BrandVideo({ src, poster, name, onError }) {
  const [playing, setPlaying] = useState(false);

  return (
    <button
      type="button"
      className={`pd-video${playing ? ' playing' : ''}`}
      onClick={() => setPlaying(true)}
      disabled={playing}
      aria-label={playing ? undefined : `Play ${name || 'brand'} video`}
    >
      {playing ? (
        <iframe
          src={src}
          title={`${name || 'Brand'} video`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <>
          <img src={poster} alt="" loading="lazy" onError={onError} />
          <span className="pv-play">{PlayIcon}</span>
        </>
      )}
    </button>
  );
}

export default function BrandAbout({
  story,
  values,
  valuesNote,
  shippingLines,
  returnPolicy,
  videoSrc,
  videoPoster,
  onPosterError,
  name,
  since,
  email,
}) {
  const hasValues = (values && values.length > 0) || !!valuesNote;
  const hasConnect = !!videoSrc || !!since || !!email;
  /* A left panel with nothing in it would still reserve a grid column: with
     Shipping/Return now only in the product drawer's About tab and no
     Overview/Brand-values content, it can render fully empty and strand Connect
     in the second column. `bd-about--solo` drops the grid. */
  const hasLeft = !!story || hasValues || (shippingLines?.length > 0) || !!returnPolicy;

  return (
    <div className={`bd-about${hasLeft ? '' : ' bd-about--solo'}`}>
      {hasLeft && (
      <div className="bd-panel">
        {story && (
          <section className="pd-sec">
            <h4>Overview</h4>
            <p className="pd-desc">{story}</p>
          </section>
        )}

        {hasValues && (
          <section className="pd-sec">
            <h4>Brand values</h4>
            {valuesNote && <p className="pd-desc">{valuesNote}</p>}
            {values?.length > 0 && (
              <div className="pd-values">
                {values.map((v) => <span className="pd-value" key={v}>{v}</span>)}
              </div>
            )}
          </section>
        )}

        {/* Lines, not a paragraph: the reference models shipping as a list
            (`shipLines(k)`) because it is two or three independent facts — what
            it costs, when it leaves. */}
        {shippingLines?.length > 0 && (
          <section className="pd-sec">
            <h4>Shipping</h4>
            <div className="pd-lines">
              {shippingLines.map((l) => <div className="pd-line" key={l}>{l}</div>)}
            </div>
          </section>
        )}

        {returnPolicy && (
          <section className="pd-sec">
            <h4>Return &amp; refund policy</h4>
            <p className="pd-desc">{returnPolicy}</p>
          </section>
        )}
      </div>
      )}

      {hasConnect && (
        <div className="bd-panel bd-connect-panel">
          <section className="pd-sec pd-connect">
            <h4>Connect</h4>
            {videoSrc && (
              <BrandVideo src={videoSrc} poster={videoPoster} name={name} onError={onPosterError} />
            )}
            {(since || email) && (
              <div className="pd-facts">
                {since && (
                  <div className="pd-fact">{CalendarIcon}<span>On ShopDot since {since}</span></div>
                )}
                {email && (
                  <div className="pd-fact">{MailIcon}<a href={`mailto:${email}`}>{email}</a></div>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/* The same two panels at the same widths, so the tab does not rearrange when the
   brand lands. */
export function BrandAboutSkeleton() {
  return (
    <div className="bd-about" aria-hidden="true">
      <div className="bd-panel">
        <div className="pd-sec">
          <Skeleton w={70} h={12} />
          <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
            <Skeleton w="100%" h={13} /><Skeleton w="96%" h={13} /><Skeleton w="62%" h={13} />
          </div>
        </div>
        {[0, 1].map((i) => (
          <div className="pd-sec" key={i}>
            <Skeleton w={i ? 118 : 86} h={12} />
            <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              <Skeleton w="88%" h={13} /><Skeleton w="54%" h={13} />
            </div>
          </div>
        ))}
      </div>
      <div className="bd-panel bd-connect-panel">
        <div className="pd-sec">
          <Skeleton w={62} h={12} />
          <div style={{ marginTop: 12 }}>
            <Skeleton w="100%" h={169} shape="tile" />
          </div>
          <div className="pd-facts">
            <Skeleton w="72%" h={12} /><Skeleton w="58%" h={12} />
          </div>
        </div>
      </div>
    </div>
  );
}

BrandAbout.propTypes = {
  story: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string),
  valuesNote: PropTypes.string,
  shippingLines: PropTypes.arrayOf(PropTypes.string),
  returnPolicy: PropTypes.string,
  videoSrc: PropTypes.string,
  videoPoster: PropTypes.string,
  onPosterError: PropTypes.func,
  name: PropTypes.string,
  since: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  email: PropTypes.string,
};

BrandVideo.propTypes = {
  src: PropTypes.string,
  poster: PropTypes.string,
  name: PropTypes.string,
  onError: PropTypes.func,
};
